import { useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, X, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function fullUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${BACKEND}${u}`;
}

/**
 * FileUploader — aceita apenas JPEG e PDF. Limite 5MB.
 * Suporta single (avatar) e multi (portfolio).
 *
 * Props:
 * - value: string | string[]   (URLs já salvas)
 * - onChange(next)             (callback)
 * - multi: bool                (multi-arquivo, retorna array)
 * - accept: string             (default "image/jpeg,application/pdf")
 * - max: number                (limite de arquivos em multi, default 20)
 */
export default function FileUploader({ value, onChange, multi = false, accept = "image/jpeg,application/pdf", max = 20, testid = "upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const list = multi ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const handle = async (files) => {
    if (!files || files.length === 0) return;
    if (multi && list.length + files.length > max) {
      return toast.error(`Máximo de ${max} arquivos.`);
    }
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/tenant/uploads", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      if (multi) onChange([...list, ...uploaded]);
      else onChange(uploaded[0]);
      toast.success(`${uploaded.length} arquivo(s) enviados.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = (i) => {
    if (multi) onChange(list.filter((_, idx) => idx !== i));
    else onChange("");
  };

  const isPdf = (u) => (u || "").toLowerCase().endsWith(".pdf");

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multi}
        onChange={(e) => handle(e.target.files)}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="w-full"
        data-testid={`${testid}-btn`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
        {multi ? "Enviar arquivos (JPEG ou PDF)" : "Enviar arquivo (JPEG ou PDF)"}
      </Button>
      <div className="text-[10px] text-muted-foreground">Máximo 5 MB por arquivo. Apenas JPEG ou PDF.</div>

      {list.length > 0 && (
        <div className={multi ? "grid grid-cols-3 sm:grid-cols-4 gap-2" : ""}>
          {list.map((u, i) => (
            <div key={i} className="relative group">
              {isPdf(u) ? (
                <a href={fullUrl(u)} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-lg border bg-muted flex flex-col items-center justify-center text-xs">
                  <FileText className="w-6 h-6 mb-1 text-muted-foreground" />
                  <span className="text-[10px]">PDF</span>
                </a>
              ) : (
                <a href={fullUrl(u)} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-lg overflow-hidden border">
                  <img src={fullUrl(u)} alt="" className="w-full h-full object-cover" />
                </a>
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { fullUrl };
